import React, { useEffect, useRef, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';
import HttpService from '../../services/httpService';
import { setLoading, setSnackInfo } from '../../redux/actions/appAction';
import { Paper, Card, CardContent, Box, Typography, TablePagination, CircularProgress, Tooltip, IconButton, TextField, MenuItem, Button } from '@mui/material';
import { makeStyles } from '@material-ui/core/styles';
import moment from 'moment';
import { CSVLink } from 'react-csv';
import XLSX from 'xlsx';
import { FileTextOutlined, DownloadOutlined, InboxOutlined, LockOutlined, FilterOutlined, CloseOutlined, FileExcelOutlined } from '@ant-design/icons';
import { ROLENAME } from '../../utils/index';

// Keep in sync with quotation_tool_server/routes/leadLog.js's TAX_INVOICE_TYPES
// and erp-solution-ui/src/pages/manageProjectLog/index.js's TAX_INVOICE_TYPES.
const TAX_INVOICE_TYPES = [
    { code: 'DSO', label: 'Designs Sign off - 20% tax Invoice' },
    { code: 'DI', label: 'Dispatch invoice - 65% Tax Invoice' },
    { code: 'CI', label: 'Completion Invoice - 15% Tax Invoice' },
    { code: 'RCN', label: 'Refund- Credit Note - for all the scope Deduction' },
    { code: 'ASI', label: 'Additional Scope - Additional Scope Invoice' },
];

const EMPTY_FILTERS = { search: '', type: '', startDate: '', endDate: '', sendBy: '', channel: '' };

// Same brand palette used on the recently-redesigned listing pages
// (e.g. callRecordings/leadCallLogs.js) so this reads consistently.
const ORANGE = "#F47E44";
const ORANGE_DARK = "#c1531f";
const ORANGE_LIGHT = "#fff1ea";
const HEADER_GRADIENT = `linear-gradient(135deg, ${ORANGE_DARK} 0%, ${ORANGE} 60%, #ff9c66 100%)`;
const GREEN_GRADIENT = `linear-gradient(135deg, #146c43, #1e8449)`;
const HEADING_FONT = "'Silka bold', 'Silka semiBold', sans-serif !important";

const useStyles = makeStyles(() => ({
    page: { padding: 20, background: "#f6f7fb", minHeight: "100vh" },
    paperContent: { display: 'flex', flexDirection: 'column', backgroundColor: 'transparent', boxShadow: 'none' },
    headingCard: {
        marginBottom: 20,
        borderRadius: 20,
        background: HEADER_GRADIENT,
        color: "#fff",
        boxShadow: "0 10px 28px rgba(196,83,31,0.2)",
    },
    headingWrapper: { display: "flex", alignItems: "center", gap: 16 },
    headerIconWrap: {
        width: 52,
        height: 52,
        borderRadius: "50%",
        background: "rgba(255,255,255,0.22)",
        border: "2px solid rgba(255,255,255,0.35)",
        boxShadow: "0 4px 14px rgba(0,0,0,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    heading: { fontWeight: 700, color: "#fff", fontFamily: HEADING_FONT },
    subHeading: { color: "rgba(255,255,255,0.85)", marginTop: 2 },
    tableCard: { borderRadius: 20, overflow: "hidden", boxShadow: "0 6px 20px rgba(17,24,39,0.06)", background: "#fff" },
    tableWrapper: { overflow: "auto", maxHeight: 800 },
    tableHeaderCell: {
        backgroundColor: ORANGE_DARK,
        color: '#ffffff',
        fontWeight: 'bold',
        fontSize: 13,
        padding: '10px',
        textAlign: 'center',
        whiteSpace: 'nowrap',
        position: 'sticky',
        top: 0,
        zIndex: 1,
    },
    tableRow: {
        "&:nth-of-type(odd)": { backgroundColor: "#fafafa" },
        "&:hover": { backgroundColor: ORANGE_LIGHT },
        transition: "background-color .12s ease",
    },
    emptyState: { padding: "60px 20px", textAlign: "center", color: "#9aa1ac" },
    emptyIconWrap: {
        width: 80,
        height: 80,
        borderRadius: "50%",
        background: ORANGE_LIGHT,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        margin: "0 auto 14px",
    },
    downloadBtn: {
        color: `${ORANGE_DARK} !important`,
        backgroundColor: `${ORANGE_LIGHT} !important`,
    },
    filterCard: {
        borderRadius: 20,
        boxShadow: "0 6px 20px rgba(17,24,39,0.06)",
        padding: "18px 22px",
        marginBottom: 20,
        background: "#fff",
    },
    filterRow: { display: "flex", flexWrap: "wrap", alignItems: "center", gap: 14 },
    submitBtn: {
        color: "#fff !important",
        background: `linear-gradient(135deg, ${ORANGE_DARK}, ${ORANGE}) !important`,
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 12,
        boxShadow: "none",
    },
    clearBtn: {
        color: "#6b7280 !important",
        border: "1.5px solid #d8dee8 !important",
        background: "#fff !important",
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 12,
    },
    exportRow: { display: "flex", justifyContent: "flex-end", gap: 10, marginBottom: 14 },
    exportBtn: {
        color: "#fff !important",
        background: `${GREEN_GRADIENT} !important`,
        textTransform: "none",
        fontWeight: 600,
        borderRadius: 12,
        boxShadow: "none",
    },
}));

const FINANCE_ROLES = [ROLENAME.FinanceManager, ROLENAME.FinanceExecutive, ROLENAME.FinanceUser, ROLENAME.Admin];

const fieldSx = (minWidth) => ({
    minWidth,
    '& .MuiOutlinedInput-root': { borderRadius: '10px' },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: ORANGE },
    '& .MuiInputLabel-root.Mui-focused': { color: ORANGE_DARK },
});

const TaxInvoicesListing = (props) => {
    const classes = useStyles();
    const { userDetails } = useSelector((state) => state.user);
    const isFinanceUser = userDetails?.roles?.some((role) => FINANCE_ROLES.includes(role.name));

    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0); // MUI TablePagination is 0-indexed
    const [rowsPerPage, setRowsPerPage] = useState(20);
    const [dataLoading, setDataLoading] = useState(false);
    const [filters, setFilters] = useState(EMPTY_FILTERS); // draft values in the filter bar
    const [appliedFilters, setAppliedFilters] = useState(EMPTY_FILTERS); // values actually queried

    const fetchTaxInvoices = async (pageIndex, limit, filterParams) => {
        setDataLoading(true);
        try {
            const response = await HttpService.getTaxInvoicesList(pageIndex + 1, limit, filterParams);
            setRows(response?.data?.data || []);
            setTotal(response?.data?.total || 0);
        } catch (error) {
            props.setSnackInfo(error?.response?.data?.message || 'Failed to load Tax Invoices.', 'error');
        }
        setDataLoading(false);
    };

    useEffect(() => {
        if (isFinanceUser) {
            fetchTaxInvoices(page, rowsPerPage, appliedFilters);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, appliedFilters, isFinanceUser]);

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleFilterChange = (field) => (e) => setFilters({ ...filters, [field]: e.target.value });
    const applyFilters = () => {
        setPage(0);
        setAppliedFilters(filters);
    };
    const clearFilters = () => {
        setFilters(EMPTY_FILTERS);
        setPage(0);
        setAppliedFilters(EMPTY_FILTERS);
    };

    const csvHeaders = [
        { key: 'slNo', label: 'SL No.' },
        { key: 'taxInvoiceTypeLabel', label: 'Type' },
        { key: 'customerName', label: 'Cx Name' },
        { key: 'uniqueLeadNumber', label: 'Unique Lead ID' },
        { key: 'taxInvoiceAttachment', label: 'Tax Invoice Link' },
        { key: 'remark', label: 'Remarks' },
        { key: 'formattedDate', label: 'Date and Time' },
        { key: 'sendBy', label: 'Send By' },
    ];
    const csvData = rows.map((row) => ({
        ...row,
        formattedDate: row.createdAt ? moment(row.createdAt).format('DD-MM-YYYY hh:mm A') : '-'
    }));
    const csvLinkRef = useRef(null);

    const downloadCsv = () => {
        if (!rows?.length) {
            props.setSnackInfo('No Tax Invoices to export.', 'error');
            return;
        }
        setTimeout(() => csvLinkRef.current?.link?.click());
    };

    const downloadExcel = () => {
        if (!rows?.length) {
            props.setSnackInfo('No Tax Invoices to export.', 'error');
            return;
        }
        const data = rows.map((row) => ({
            'SL No.': row.slNo,
            'Type': row.taxInvoiceTypeLabel,
            'Cx Name': row.customerName,
            'Unique Lead ID': row.uniqueLeadNumber,
            'Tax Invoice Link': row.taxInvoiceAttachment || '-',
            'Remarks': row.remark || '-',
            'Date and Time': row.createdAt ? moment(row.createdAt).format('DD-MM-YYYY hh:mm A') : '-',
            'Send By': row.sendBy,
        }));
        const ws = XLSX.utils.json_to_sheet(data);
        // Column E = "Tax Invoice Link" — keep the raw S3 URL as the cell text
        // and also make it a clickable hyperlink in Excel.
        rows.forEach((row, i) => {
            const cellRef = `E${i + 2}`;
            if (row.taxInvoiceAttachment) {
                ws[cellRef] = { t: 's', v: row.taxInvoiceAttachment, l: { Target: row.taxInvoiceAttachment } };
            }
        });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Tax Invoices');
        XLSX.writeFile(wb, `Tax_Invoices_${moment().format('YYYYMMDD_HHmmss')}.xlsx`);
    };

    if (!isFinanceUser) {
        return (
            <div className={classes.page}>
                <Box className={classes.emptyState}>
                    <Box className={classes.emptyIconWrap}>
                        <LockOutlined style={{ fontSize: 34, color: ORANGE_DARK }} />
                    </Box>
                    <Typography style={{ fontWeight: 600, color: "#7d8794" }}>
                        This page is only available to the Finance team.
                    </Typography>
                </Box>
            </div>
        );
    }

    return (
        <div className={classes.page}>
            <Paper className={classes.paperContent}>
                <Card className={classes.headingCard}>
                    <CardContent>
                        <Box className={classes.headingWrapper}>
                            <div className={classes.headerIconWrap}>
                                <FileTextOutlined style={{ fontSize: 24, color: "#fff" }} />
                            </div>
                            <div>
                                <Typography variant="h5" className={classes.heading}>Tax Invoices</Typography>
                                <Typography variant="body2" className={classes.subHeading}>
                                    Every GST/Tax Invoice uploaded against a lead, across all projects.
                                </Typography>
                            </div>
                        </Box>
                    </CardContent>
                </Card>

                <Box className={classes.filterCard}>
                    <Box className={classes.filterRow}>
                        <TextField
                            sx={fieldSx(230)}
                            size="small"
                            variant="outlined"
                            label="Cx Name / Unique Lead ID"
                            value={filters.search}
                            onChange={handleFilterChange('search')}
                        />
                        <TextField
                            sx={fieldSx(240)}
                            size="small"
                            variant="outlined"
                            select
                            label="Invoice Type"
                            value={filters.type}
                            onChange={handleFilterChange('type')}
                        >
                            <MenuItem value="">All</MenuItem>
                            {TAX_INVOICE_TYPES.map((option) => (
                                <MenuItem key={option.code} value={option.code}>{option.label}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            sx={fieldSx(160)}
                            size="small"
                            variant="outlined"
                            type="date"
                            label="From"
                            InputLabelProps={{ shrink: true }}
                            value={filters.startDate}
                            onChange={handleFilterChange('startDate')}
                        />
                        <TextField
                            sx={fieldSx(160)}
                            size="small"
                            variant="outlined"
                            type="date"
                            label="To"
                            InputLabelProps={{ shrink: true }}
                            value={filters.endDate}
                            onChange={handleFilterChange('endDate')}
                        />
                        <TextField
                            sx={fieldSx(180)}
                            size="small"
                            variant="outlined"
                            label="Send By"
                            value={filters.sendBy}
                            onChange={handleFilterChange('sendBy')}
                        />
                        <TextField
                            sx={fieldSx(220)}
                            size="small"
                            variant="outlined"
                            select
                            label="Communication Channel"
                            value={filters.channel}
                            onChange={handleFilterChange('channel')}
                        >
                            <MenuItem value="">All</MenuItem>
                            <MenuItem value="email">Email</MenuItem>
                            <MenuItem value="whatsapp">WhatsApp</MenuItem>
                            <MenuItem value="both">Email + WhatsApp</MenuItem>
                        </TextField>
                        <Box sx={{ display: 'flex', gap: '10px', flexShrink: 0 }}>
                            <Button
                                variant="contained"
                                className={classes.submitBtn}
                                startIcon={<FilterOutlined />}
                                onClick={applyFilters}
                                sx={{ height: 40, whiteSpace: 'nowrap' }}
                            >
                                Apply
                            </Button>
                            <Button
                                variant="outlined"
                                className={classes.clearBtn}
                                startIcon={<CloseOutlined />}
                                onClick={clearFilters}
                                sx={{ height: 40, whiteSpace: 'nowrap' }}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Box>
                </Box>

                <Box className={classes.exportRow}>
                    <Button
                        variant="contained"
                        className={classes.exportBtn}
                        startIcon={<DownloadOutlined />}
                        onClick={downloadCsv}
                    >
                        CSV
                    </Button>
                    <Button
                        variant="contained"
                        className={classes.exportBtn}
                        startIcon={<FileExcelOutlined />}
                        onClick={downloadExcel}
                    >
                        Excel
                    </Button>
                    <CSVLink
                        headers={csvHeaders}
                        data={csvData}
                        ref={csvLinkRef}
                        filename={`Tax_Invoices_${moment().format('YYYYMMDD_HHmmss')}.csv`}
                        style={{ display: 'none' }}
                    />
                </Box>

                <Box className={classes.tableCard}>
                    {dataLoading ? (
                        <Box style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "60px 0" }}>
                            <CircularProgress style={{ color: ORANGE }} />
                            <Typography style={{ color: "#9aa1ac", marginTop: 10 }}>Loading Tax Invoices...</Typography>
                        </Box>
                    ) : !rows?.length ? (
                        <Box className={classes.emptyState}>
                            <Box className={classes.emptyIconWrap}>
                                <InboxOutlined style={{ fontSize: 34, color: ORANGE_DARK }} />
                            </Box>
                            <Typography style={{ fontWeight: 600, color: "#7d8794" }}>
                                No Tax Invoices uploaded yet.
                            </Typography>
                        </Box>
                    ) : (
                        <>
                            <div className={classes.tableWrapper}>
                                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                                    <thead>
                                        <tr>
                                            <th className={classes.tableHeaderCell}>SL No.</th>
                                            <th className={classes.tableHeaderCell}>Type</th>
                                            <th className={classes.tableHeaderCell}>Cx Name</th>
                                            <th className={classes.tableHeaderCell}>Unique Lead ID</th>
                                            <th className={classes.tableHeaderCell}>Tax Invoice</th>
                                            <th className={classes.tableHeaderCell}>Remarks</th>
                                            <th className={classes.tableHeaderCell}>Date and Time</th>
                                            <th className={classes.tableHeaderCell}>Send By</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((row) => (
                                            <tr key={`${row.uniqueLeadNumber}-${row.slNo}`} className={classes.tableRow}>
                                                <td style={{ padding: '10px', textAlign: 'center', fontSize: 13.5 }}>{row.slNo}</td>
                                                <td style={{ padding: '10px', textAlign: 'center', fontSize: 13.5 }}>{row.taxInvoiceTypeLabel}</td>
                                                <td style={{ padding: '10px', textAlign: 'center', fontSize: 13.5 }}>{row.customerName}</td>
                                                <td style={{ padding: '10px', textAlign: 'center', fontSize: 13.5 }}>
                                                    {row.leadId ? (
                                                        <NavLink to={`/project-logs/${row.leadId}`} style={{ color: ORANGE_DARK, fontWeight: 600 }}>
                                                            {row.uniqueLeadNumber}
                                                        </NavLink>
                                                    ) : row.uniqueLeadNumber}
                                                </td>
                                                <td style={{ padding: '10px', textAlign: 'center' }}>
                                                    {row.taxInvoiceAttachment ? (
                                                        <Tooltip title="Download Tax Invoice">
                                                            <IconButton
                                                                size="small"
                                                                component="a"
                                                                href={row.taxInvoiceAttachment}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className={classes.downloadBtn}
                                                            >
                                                                <DownloadOutlined style={{ fontSize: 16 }} />
                                                            </IconButton>
                                                        </Tooltip>
                                                    ) : '-'}
                                                </td>
                                                <td style={{ padding: '10px', textAlign: 'center', fontSize: 13.5, maxWidth: 240 }}>{row.remark || '-'}</td>
                                                <td style={{ padding: '10px', textAlign: 'center', fontSize: 13.5, whiteSpace: 'nowrap' }}>
                                                    {row.createdAt ? moment(row.createdAt).format('DD-MM-YYYY hh:mm A') : '-'}
                                                </td>
                                                <td style={{ padding: '10px', textAlign: 'center', fontSize: 13.5 }}>{row.sendBy}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <TablePagination
                                rowsPerPageOptions={[20, 50, 100]}
                                count={total}
                                rowsPerPage={rowsPerPage}
                                page={page}
                                component="div"
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                            />
                        </>
                    )}
                </Box>
            </Paper>
        </div>
    );
};

export default connect(null, { setLoading, setSnackInfo })(TaxInvoicesListing);
